import React from 'react';
import { Box, Typography, Container } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  GlassHeroContainer,
} from '../../components/glassmorphism/GlassmorphismStyles';
import GetStarted from '../../components/GetStarted/GetStarted';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <GlassHeroContainer>
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: { xs: 14, sm: 20 },
          pb: { xs: 8, sm: 12 },
        }}
      >
        <PrivacyContainer>
          {/* Header Section */}
          <HeaderSection>
            <HeadingWrapper>
              <MainHeading>Word of Mouth Ltd</MainHeading>
              <SubHeaderRow>
                <SubHeading>Privacy Policy</SubHeading>
                <LastUpdated>Last updated: 04 June 2025</LastUpdated>
              </SubHeaderRow>
            </HeadingWrapper>
          </HeaderSection>

          {/* Section 1: Introduction */}
          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>1. Introduction</SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                This Privacy Policy explains how Word of Mouth Ltd ("we", "us", or "our") collects, uses, and protects personal data of users of our platform (the "Platform").
                <br />
                We are committed to ensuring that your privacy is protected in accordance with applicable data protection laws, including the UK General Data Protection Regulation (UK GDPR).
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>

          {/* Section 2: Data Controller */}
          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>2. Data Controller</SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                Word of Mouth Ltd (Company No. 16267512)
                <br />
                Registered office: Cambridge House, Highgate, Forest Row, East Sussex, RH18 5BA, United Kingdom
                <br />
                Email: support@womapp.uk
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>

          {/* Section 3: What data we collect */}
          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>3. What data we collect</SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                We may collect the following personal data:
                <br />
                - Name
                <br />
                - Email address
                <br />
                - Phone number
                <br />
                - Identity verification data (ID documents, biometric data such as selfies) processed via Persona
                <br />
                - Service usage data (such as service categories: handyman, cleaning, personal care, gardening, home repairs)
                <br />
                - Communication and transaction history
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>

          {/* Section 4: How we use your data */}
          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>4. How we use your data</SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                We use personal data to:
                <br />
                - Verify the identity of service providers and clients
                <br />
                - Provide and operate the Platform
                <br />
                - Ensure trust and safety for all users
                <br />
                - Comply with legal obligations
                <br />
                - Communicate with users
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>

          {/* Section 5: Legal basis for processing */}
          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>5. Legal basis for processing</SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                We process personal data based on the following legal bases:
                <br />
                - Performance of a contract
                <br />
                - Legal obligation
                <br />
                - Legitimate interests
                <br />
                - Consent (where applicable)
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>

          {/* Section 6: Data sharing */}
          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>6. Data sharing</SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                We may share personal data with:
                <br />
                - Persona, our third-party identity verification provider
                <br />
                - Payment processors
                <br />
                - Service providers supporting our Platform operations
                <br />
                - Law enforcement or regulatory authorities when required by law
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>

          {/* Section 7: International data transfers */}
          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>7. International data transfers</SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                We may process personal data using trusted third-party service providers, some of whom may operate outside of the United Kingdom or the European Economic Area (EEA). In such cases, we ensure appropriate safeguards are in place, such as Standard Contractual Clauses (SCCs), to protect personal data in compliance with applicable data protection laws.
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>

          {/* Section 8: Data retention */}
          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>8. Data retention</SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                We retain personal data only as long as necessary to provide our services and meet legal or regulatory requirements.
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>

          {/* Section 9: Your rights */}
          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>9. Your rights</SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                You have the following rights under the UK GDPR:
                <br />
                - Right to access your personal data
                <br />
                - Right to rectify inaccurate data
                <br />
                - Right to erase your data (subject to legal limitations)
                <br />
                - Right to object to or restrict processing
                <br />
                - Right to data portability
                <br />
                To exercise your rights, please contact us at support@womapp.uk
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>

          {/* Section 10: Updates to this policy */}
          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>10. Updates to this policy</SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                We may update this Privacy Policy from time to time. The updated version will be posted on our Platform. Continued use of the Platform constitutes acceptance of the updated policy.
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>

          {/* Section 11: Contact */}
          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>11. Contact</SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                If you have any questions about this Privacy Policy or how we handle your personal data, please contact us at:
                <br />
                Word of Mouth Ltd
                <br />
                Email: support@womapp.uk
                <br />
                Registered office: Cambridge House, Highgate, Forest Row, East Sussex, RH18 5BA, United Kingdom
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>
        </PrivacyContainer>

        {/* Get Started CTA */}
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mt: 8, mb: 6 }}>
          <GetStarted />
        </Box>
      </Container>
    </GlassHeroContainer>
  );
};

// Main container - Frame 8
const PrivacyContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '0px',
  gap: '24px',
  width: '100%',
  maxWidth: '1150px',

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
    padding: '0 20px',
  },
}));

// Frame 4 - Header Section
const HeaderSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0px',
  gap: '48px',
  width: '100%',
  maxWidth: '1150px',

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

// Frame 5 - Heading Wrapper
const HeadingWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0px',
  gap: '16px',
  width: '100%',
  maxWidth: '760px',

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

// Main Heading - Word of Mouth Ltd
const MainHeading = styled(Typography)(({ theme }) => ({
  width: '100%',
  maxWidth: '760px',
  fontFamily: 'Montserrat',
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: '60px',
  lineHeight: '120%',
  textAlign: 'center',
  letterSpacing: '-0.5px',
  color: theme.palette.mode === 'dark' ? '#00FFFF' : '#005CAD',

  [theme.breakpoints.down('md')]: {
    fontSize: '48px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '36px',
  },
}));

// Frame 6 - Sub Header Row
const SubHeaderRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  padding: '0px',
  gap: '16px',
  width: '100%',
  maxWidth: '760px',

  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
}));

// Sub Heading - Privacy Policy
const SubHeading = styled(Typography)(({ theme }) => ({
  margin: '0 auto',
  fontFamily: 'Montserrat',
  fontStyle: 'normal',
  fontWeight: 700,
  fontSize: '24px',
  lineHeight: '133.4%',
  textAlign: 'center',
  color: theme.palette.mode === 'dark' ? '#E5E5E5' : '#1A1A1A',

  [theme.breakpoints.down('sm')]: {
    fontSize: '20px',
  },
}));

// Last Updated
const LastUpdated = styled(Typography)(({ theme }) => ({
  margin: '0 auto',
  fontFamily: 'Roboto',
  fontStyle: 'normal',
  fontWeight: 700,
  fontSize: '16px',
  lineHeight: '175%',
  textAlign: 'center',
  letterSpacing: '0.15px',
  color: theme.palette.mode === 'dark' ? '#B6B6B6' : '#1A1A1A',

  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
  },
}));

// Content Section
const ContentSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0px',
  gap: '8px',
  width: '100%',
  maxWidth: '1150px',

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

// Section Title Wrapper
const SectionTitleWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0px',
  gap: '16px',
  width: '100%',
  maxWidth: '760px',

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

// Section Title
const SectionTitle = styled(Typography)(({ theme }) => ({
  width: '100%',
  fontFamily: 'Roboto',
  fontStyle: 'normal',
  fontWeight: 700,
  fontSize: '16px',
  lineHeight: '175%',
  letterSpacing: '0.15px',
  color: theme.palette.mode === 'dark' ? '#00FFFF' : '#005CAD',
  textAlign: 'left',
}));

// Section Content Wrapper
const SectionContentWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0px',
  gap: '16px',
  width: '100%',
  maxWidth: '760px',

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

// Body Text
const BodyText = styled(Typography)(({ theme }) => ({
  width: '100%',
  fontFamily: 'Roboto',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '150%',
  letterSpacing: '0.15px',
  color: theme.palette.mode === 'dark' ? '#B6B6B6' : '#1A1A1A',
  textAlign: 'left',
}));

export default PrivacyPolicyPage;
